import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { clinic, navItems, treatments } from '../data/site.js';
import { PhoneIcon, WhatsAppIcon } from './ContactIcons.jsx';

function SmartLink({ href, className, children, onClick, ...props }) {
  const isExternal = /^https?:|^mailto:|^tel:/i.test(href);

  if (isExternal) {
    return (
      <a href={href} className={className} onClick={onClick} {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className} onClick={onClick} {...props}>
      {children}
    </Link>
  );
}

function CalendarIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function SocialIcon({ link }) {
  const icons = {
    Facebook: (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M14.2 8.05V6.7c0-.67.16-1.06 1.08-1.06h1.36V3.2A18.6 18.6 0 0 0 14.65 3c-1.97 0-3.32 1.2-3.32 3.41v1.64H9.1v2.73h2.23V18h2.73v-7.22h2.14l.34-2.73h-2.34Z" />
      </svg>
    ),
    Instagram: (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="5" y="5" width="14" height="14" rx="4" ry="4" />
        <circle cx="12" cy="12" r="3.35" />
        <circle cx="16.15" cy="7.85" r="0.85" />
      </svg>
    ),
    YouTube: (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M21.15 8.1a3.03 3.03 0 0 0-2.13-2.14C17.15 5.45 12 5.45 12 5.45s-5.15 0-7.02.51A3.03 3.03 0 0 0 2.85 8.1a31.7 31.7 0 0 0 0 7.8 3.03 3.03 0 0 0 2.13 2.14c1.87.51 7.02.51 7.02.51s5.15 0 7.02-.51a3.03 3.03 0 0 0 2.13-2.14 31.7 31.7 0 0 0 0-7.8Z" />
        <path d="m10.2 14.9 4.68-2.9-4.68-2.9v5.8Z" />
      </svg>
    ),
    WhatsApp: <WhatsAppIcon />
  };
  const content = icons[link.label] || null;
  if (!link.href) {
    return (
      <span className={`footer-social__icon footer-social__icon--${link.label.toLowerCase()} is-disabled`} title={`${link.label} profile coming soon`}>
        {content}
      </span>
    );
  }

  return (
    <a className={`footer-social__icon footer-social__icon--${link.label.toLowerCase()}`} href={link.href} target="_blank" rel="noreferrer" aria-label={link.label}>
      {content}
    </a>
  );
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const hasLightHero = isHomePage || location.pathname.startsWith('/dentists/');

  useEffect(() => {
    let ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 10);
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setScrolled(window.scrollY > 10);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    setMenuOpen(false);
    document.body.style.overflow = '';
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <header className={`site-header ${isHomePage ? 'site-header--home' : ''} ${hasLightHero ? 'site-header--light-hero' : ''} ${scrolled ? 'is-scrolled' : ''} ${menuOpen ? 'has-open-menu' : ''}`} role="banner">
        <div className="container header-inner">
          <Link to="/" className="header-logo" aria-label={`${clinic.name} home`}>
            <img src={clinic.logo} alt={`${clinic.name} logo`} width="344" height="86" />
            <span className="header-logo__tagline">A superspeciality Dental Clinic</span>
          </Link>

          <nav className="header-nav" aria-label="Main navigation">
            {navItems.map((item) => (
              <SmartLink key={item.href} href={item.href} className="header-nav__link">
                {item.label}
              </SmartLink>
            ))}
          </nav>

          <div className="header-cta">
            <SmartLink href="/#booking" className="btn btn--primary">
              Book Appointment
            </SmartLink>
          </div>

          <div className="header-mobile-actions" aria-label="Quick contact">
            <a href={clinic.phoneHref} className="header-mobile-action" aria-label={`Call ${clinic.phoneDisplay}`}>
              <PhoneIcon />
            </a>
            <a href={clinic.whatsappHref} className="header-mobile-action header-mobile-action--whatsapp" aria-label="Message on WhatsApp">
              <WhatsAppIcon />
            </a>
          </div>

          <button
            type="button"
            className={`menu-toggle ${menuOpen ? 'is-active' : ''}`}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="menu-toggle__bar" />
            <span className="menu-toggle__bar" />
            <span className="menu-toggle__bar" />
          </button>
        </div>
      </header>

      <div className={`mobile-nav-overlay ${menuOpen ? 'is-open' : ''}`} id="mobile-nav" role="dialog" aria-label="Mobile navigation">
        <button type="button" className="mobile-nav-overlay__close" onClick={() => setMenuOpen(false)}>
          Close
        </button>
        {navItems.map((item) => (
          <SmartLink key={item.href} href={item.href} className="mobile-nav-overlay__link" onClick={() => setMenuOpen(false)}>
            {item.label}
          </SmartLink>
        ))}
        <SmartLink href="/#booking" className="btn btn--primary" onClick={() => setMenuOpen(false)}>
          Book Appointment
        </SmartLink>
      </div>
    </>
  );
}

function Footer() {
  return (
    <footer className="site-footer" role="contentinfo">
      <div className="container">
        <div className="footer__inner">
          <div className="footer__brand">
            <img className="footer__logo" src={clinic.logo} alt={`${clinic.name} logo`} width="344" height="86" />
            <p className="footer__brand-text">{clinic.description}</p>
          </div>

          <div className="footer__col">
            <p className="footer__col-heading">Treatments</p>
            <div className="footer__col-links">
              {treatments.map((treatment) => (
                <Link key={treatment.slug} to={`/treatments/${treatment.slug}/`}>
                  {treatment.title}
                </Link>
              ))}
            </div>
          </div>

          <div className="footer__col">
            <p className="footer__col-heading">Doctor</p>
            <div className="footer__col-links">
              <Link to="/#dentist">The Dentist</Link>
              <Link to="/#booking">New Patients</Link>
              <Link to="/gallery/">Gallery</Link>
              <Link to="/blog/">Blog</Link>
              <Link to="/#location">Contact</Link>
            </div>
          </div>

          <div className="footer__col">
            <p className="footer__col-heading">Contact</p>
            <div className="footer__col-links">
              <a href={clinic.phoneHref}>{clinic.phoneDisplay}</a>
              <a href={clinic.emailHref}>{clinic.emailDisplay}</a>
              <a href={clinic.whatsappHref}>WhatsApp</a>
              <a href={clinic.mapUrl} target="_blank" rel="noreferrer">Google Maps</a>
              <a href={clinic.gmbUrl} target="_blank" rel="noreferrer">Google Business Profile</a>
              <div className="footer-social" aria-label="Social media profiles">
                {clinic.socialLinks.map((link) => (
                  <SocialIcon key={link.label} link={link} />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__copyright">(c) 2026 {clinic.name}. All rights reserved.</p>
          <p className="footer__credit">
            Website developed by{' '}
            <a href="https://ariesmediacompany.com" target="_blank" rel="noreferrer">
              Aries Media Company
            </a>
          </p>
          <div className="footer__legal">
            <Link to="/privacy-policy/">Privacy Policy</Link>
            <Link to="/cookie-policy/">Cookie Policy</Link>
            <Link to="/accessibility/">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FloatingBookingButton() {
  const [bookingInView, setBookingInView] = useState(false);
  const location = useLocation();
  const hideOnDoctorProfile = location.pathname.startsWith('/dentists/');

  useEffect(() => {
    function updateVisualViewportWidth() {
      const width = window.visualViewport?.width || window.innerWidth;
      document.documentElement.style.setProperty('--md-visual-viewport-width', `${Math.round(width)}px`);
    }

    updateVisualViewportWidth();
    window.visualViewport?.addEventListener('resize', updateVisualViewportWidth);
    window.addEventListener('resize', updateVisualViewportWidth);
    return () => {
      window.visualViewport?.removeEventListener('resize', updateVisualViewportWidth);
      window.removeEventListener('resize', updateVisualViewportWidth);
    };
  }, []);

  useEffect(() => {
    const target = document.getElementById('booking');
    if (!target) {
      setBookingInView(false);
      return undefined;
    }

    function updateVisibility() {
      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      setBookingInView(rect.top < viewportHeight * 0.72 && rect.bottom > 110);
    }

    updateVisibility();
    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(([entry]) => setBookingInView(entry.isIntersecting), {
          rootMargin: '-90px 0px -38% 0px',
          threshold: 0.01
        })
      : null;

    if (observer) {
      observer.observe(target);
      return () => observer.disconnect();
    }

    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    return () => {
      window.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [location.pathname, location.hash]);

  return (
    <Link to="/#booking" className={`floating-booking ${bookingInView || hideOnDoctorProfile ? 'is-hidden' : ''}`} aria-label="Book an appointment">
      <CalendarIcon />
    </Link>
  );
}

function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.localStorage.getItem('my-dentist-cookie-consent')) return;
    const timer = window.setTimeout(() => setVisible(true), 900);
    return () => window.clearTimeout(timer);
  }, []);

  function choose(value) {
    window.localStorage.setItem('my-dentist-cookie-consent', value);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="cookie-banner is-visible" role="dialog" aria-label="Cookie consent">
      <div className="cookie-banner__inner">
        <p className="cookie-banner__text">
          We use essential cookies to make this website work. Analytics cookies should only be enabled after the clinic connects its consent and analytics setup.
        </p>
        <div className="cookie-banner__actions">
          <button className="cookie-banner__btn cookie-banner__btn--accept" type="button" onClick={() => choose('accepted')}>
            Accept
          </button>
          <button className="cookie-banner__btn cookie-banner__btn--decline" type="button" onClick={() => choose('declined')}>
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Header />
      <main id="main-content">
        <Outlet />
      </main>
      <Footer />
      <FloatingBookingButton />
      <CookieBanner />
    </>
  );
}
